import React from 'react';
import { saleDataForDisplay } from '../../util/saleOperations';

// Utils
import {
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';

import CustomExtendedDataSection from '../../components/CustomExtendedDataSection/CustomExtendedDataSection.js';
import { localizeEnumOptions } from '../../util/listingFieldLabels.js';
import {
  MANDATO_LISTING_TYPE,
  MANDATO_RANGE_GROUPS,
  MANDATO_SET_KEYS,
  formatScaleRange,
  formatSet,
} from './mandatoDetails.js';

/**
 * Renders custom listing fields.
 * - SectionDetails is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnum is used if schemaType is 'multi-enum'
 * - SectionText is used if schemaType is 'text'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const { metadata, listingFieldConfigs, categoryConfiguration, intl } = props;
  const publicData = saleDataForDisplay(props.publicData);

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  // Fields are shown by default. Set showConfig.displayOnListingPage to false to explicitly hide a field.
  const displayableFieldConfigs = listingFieldConfigs.filter(
    fieldConfig => fieldConfig.showConfig?.displayOnListingPage !== false
  );

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };
  // FASE2-WIZARD-2b: en el mandato, los conjuntos (tipos de operación y
  // modalidades) se leen como fila de detalle —con "Cualquiera" si están todos
  // marcados— en vez de como sección de chips, para que toda la información del
  // mandato quede en una sola tabla. Se excluyen aquí para que no salgan dos
  // veces; pickExtendedDataFields los emite más abajo.
  const isMandato = publicData?.listingType === MANDATO_LISTING_TYPE;
  const propsForCustomFields = (
    pickCustomFieldProps(
      { publicData, metadata },
      displayableFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || []
  ).filter(p => !(isMandato && MANDATO_SET_KEYS.includes(p.key)));

  const sectionDetailsProps = {
    ...props,
    publicData,
    isFieldForCategory: isFieldForSelectedCategories,
    fieldConfigs: displayableFieldConfigs,
    heading: isMandato ? 'ListingPage.purchaseCriteriaTitle' : 'ListingPage.detailsTitle',
  };

  const pickExtendedDataFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData.listingType;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(currentCategories, config);

    const { isDetail, label } = showConfig;

    // FASE2-WIZARD-2b — lectura del mandato.
    if (isMandato && isTargetListingType) {
      const rangeGroup = MANDATO_RANGE_GROUPS.find(g => g.minKey === key || g.maxKey === key);
      if (rangeGroup) {
        // El par min/max se lee como UNA fila: se emite en la clave del mínimo
        // y se descarta la del máximo.
        if (key === rangeGroup.maxKey) {
          return filteredConfigs;
        }
        const text = formatScaleRange(
          rangeGroup,
          publicData[rangeGroup.minKey],
          publicData[rangeGroup.maxKey],
          intl
        );
        const rangeLabel = intl.formatMessage({ id: rangeGroup.labelId });
        return text
          ? filteredConfigs.concat({ key, value: text, label: rangeLabel })
          : filteredConfigs;
      }
      if (MANDATO_SET_KEYS.includes(key)) {
        // FASE2-COPY-01: mismas opciones que el anuncio de venta, etiquetas
        // propias del lado del comprador. Ver localizeEnumOptions.
        const text = formatSet(localizeEnumOptions(key, enumOptions, intl), publicData[key], intl);
        return text ? filteredConfigs.concat({ key, value: text, label }) : filteredConfigs;
      }
    }

    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      const detailValue = getDetailCustomFieldValue(
        // FASE2-COPY-01 / 01.4: las etiquetas visibles viven en es.json cuando
        // el campo las tiene propias (ver localizeEnumOptions). Se aplica
        // también aquí, y no solo en los conjuntos del mandato, para que la
        // ficha y el wizard no puedan divergir.
        localizeEnumOptions(key, enumOptions, intl),
        value,
        schemaType,
        key,
        label,
        intl,
        'ListingPage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={propsForCustomFields}
      idPrefix="listingPage"
      pickExtendedDataFields={pickExtendedDataFields}
    />
  );
};

export default CustomListingFields;
